import { Injectable, OnDestroy } from '@angular/core';
import { AppConfig } from '../app.config';
import { environment } from '../../environments/environment';
import { UserSessionData, User } from '../_models';
import { BehaviorSubject, Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';

@Injectable()
export class AuthenticationService extends BaseService implements OnDestroy {

  public userSessionData: UserSessionData = new UserSessionData();
  public currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  constructor(
    private http: HttpClient, 
    private config: AppConfig,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
    this.setUserSessionData();
    window.addEventListener('storage', this.storageListener);
    //this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentUser')));
    //this.currentUser = this.currentUserSubject.asObservable();
  }

  login(username: string, password: string, companyType: string) {
    return this.http.post(environment.apiUrl + 'api/user/authenticate', 
    { "username": username, "password": password, "companyType": companyType }, 
    this.prepareAnonymousOptions({ "username": username, "password": password, "companyType": companyType })).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('login'))
    );
  }

  authenticateAdUser(username: string, companyType: string, entraObjectId: string, idToken: string) {
    // The request hash is computed over the body that is sent, so build it ONCE and hand the same
    // object to both arguments — two separate literals would drift the moment one gains a field the
    // other lacks, and the mismatch surfaces as a 401 from HashAuthorization, not as a bad request.
    //
    // idToken is the Microsoft ID token MSAL obtained at sign-in. The API validates it and takes the
    // person from it alone; username and entraObjectId are kept only for an API that predates that.
    const body: any = { "username": username, "companyType": companyType, "idToken": idToken };

    if (entraObjectId) {
      body.entraObjectId = entraObjectId;
    }

    return this.http.post(environment.apiUrl + 'api/user/authenticateAdUser',
    body,
    this.prepareAnonymousOptions(body)).pipe(
      map((data: any) => {
        return data.body;
      }), catchError(this.handleError('authenticateAdUser'))
    );
  }

  public setUser(user) {
    // let user = response.json();
    if (user && user.token) {
      // store user details and jwt token in local storage to keep user logged in between page refreshes
      localStorage.setItem('currentUser', JSON.stringify(user));
      this.hashManagerService.setUserHash(user)
      this.setUserSessionData();
      this.currentUserSubject.next(user);
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('sessionsSearch');
    sessionStorage.removeItem('actionSearch');
    sessionStorage.removeItem('submissionSearch');
    sessionStorage.removeItem('sessionPageSearch');
    sessionStorage.removeItem('dashboardSearch');
    localStorage.clear();
    sessionStorage.clear();
    this.setUserSessionData();
  }

  setUserSessionData() {
    var currUser = localStorage.getItem('currentUser');
    var currentUser;
    if (currUser !== 'undefined' && currUser !== 'null' && currUser != null) {
      currentUser = JSON.parse(currUser);
      this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(currUser));
      this.currentUser = this.currentUserSubject.asObservable();
    }
    else {
      currentUser = null;
    }

    this.userSessionData.currentUser = currentUser;

  }

  /**
   * Bug 40452: the signed-in user is held in localStorage, which every tab shares. When another tab
   * signs out, or signs in as someone else, this tab must not carry on as a user other than the one it
   * shows: it goes to the login page, or reloads as the new user.
   *
   * Only a change of user counts. TokenRefreshService rewrites currentUser with a new token for the
   * same user, and that must not disturb this tab.
   */
  onStorageChanged(event: StorageEvent): void {
    // key is null when another tab cleared storage (logout() ends with localStorage.clear()).
    if (event.storageArea !== localStorage || (event.key !== 'currentUser' && event.key !== null)) {
      return;
    }
    const shownUser = this.userSessionData.currentUser;
    if (!shownUser || this.leavingForOtherTab) {
      return;
    }

    const storedUser = AuthenticationService.parseUser(
      event.key === null ? localStorage.getItem('currentUser') : event.newValue);

    if (!storedUser) {
      this.leavingForOtherTab = true;
      this.goToLogin();
    } else if (AuthenticationService.userIdentity(storedUser) !== AuthenticationService.userIdentity(shownUser)) {
      this.leavingForOtherTab = true;
      this.reloadPage();
    }
  }

  private leavingForOtherTab = false;
  private readonly storageListener = (event: StorageEvent) => this.onStorageChanged(event);

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener);
  }

  // Full page loads, so nothing of the previous user stays in memory.
  protected goToLogin(): void {
    window.location.assign('/login');
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  private static userIdentity(user: any): unknown {
    return user?.id ?? user?.username ?? null;
  }

  private static parseUser(value: string | null): any {
    try {
      return value && value !== 'undefined' ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  }

}
